import { classes } from "components/Utility";
import React, { Component, JSXElementConstructor, ReactElement, ReactNode } from "react";
import { ButtonProps } from "react-md";
import DialogContainer from "react-md/lib/Dialogs";

export interface MDialogAction {
  code?: string,
  icon?: string,
  helpText?: string,
  onClick?: () => void,
  order?: number,
}

export interface MDialogProps {
  id?: string,
  className?: string,
  contentClassName?: string,
  title?: ReactNode,
  actions?: (MDialogAction | string)[], // pass just a string to use it as a code
  onHide?: () => void,
  // this type comes from react md dialog
  contentActions?: ReactElement<any, string | JSXElementConstructor<any>> | ButtonProps | (ReactElement<any, string | JSXElementConstructor<any>> | ButtonProps)[],
  children?: ReactNode, // Using withRouter as a child of MDialog will cause this to crash, not sure why
  focusOnMount?: boolean,
  width?: number | string,
  dontSpecifyWidth?: boolean,
  useFlexInsteadOfTransformForCentering?: boolean, // dialog usually centers itself with css transform. this will stop that, so we can center it with flexbox instead. it was causing issues because getClientBoundingRect doesnt calc right if the element is in something transformed with css transform. so our drag and drop wasnt working
  visible?: boolean,  // Use this to control if the dialog is shown or hidden. If not provided defaults to true.
  ariaLabel?: string, // Used to pass down accessibility features to DialogContainer
}

class MDialog extends Component<MDialogProps> {
  static defaultProps = {
    visible: true,
    onHide: () => {},
  };
  
  constructor(props: MDialogProps) {
    super(props);

    this.getAction = this.getAction.bind(this);
    this.getGridActions = this.getGridActions.bind(this);
  }

  getAction(a) {
    let action = a;

    if (typeof a === "string") action = { code: a };

    let actionDefaults;
    switch (action.code) {
      case ACTION_CLOSE:
        actionDefaults = {
          icon: "close",
          helpText: "Close",
          order: 9600,
        };
        break;
      case ACTION_APPLY:
        actionDefaults = {
          icon: "done",
          helpText: "Apply",
          order: 9700,
        };
        break;
      case ACTION_CLONE:
        actionDefaults = {
          icon: "queue",
          helpText: "Save as New Item", order: 9700,
        };
        break;
      case ACTION_DELETE:
        actionDefaults = {
          icon: "delete_forever",
          helpText: "Save as New Item", order: 9700,
        };
        break;
      case ACTION_ADDITIONAL_OPTIONS:
        actionDefaults = {
          icon: "spellcheck",
          helpText: "Additional Options", order: 9700,
        };
        break;
      default:
        actionDefaults = {};
        break;
    }

    return { ...actionDefaults, ...action };
  }

  getGridActions() {
    let actions = this.props.actions ? this.props.actions.map(a => this.getAction(a)) : [];
    actions.sort((a, b) => a.order > b.order ? 1 : -1);
    return actions.map((a, i) => (
      <i key={`action_${i}`} className="md-icon material-icons dialog-button" title={a.helpText} onClick={a.onClick}>
        {a.icon}
      </i>));
  }

  render() {
    const { id, className, contentClassName, visible, focusOnMount, contentActions, title, width, dontSpecifyWidth, children, ariaLabel, onHide, useFlexInsteadOfTransformForCentering } = this.props;
    return (
      <DialogContainer
        {...{
          // This is called if the user clicks outside the dialog
          onHide,
          visible
        }}
        id={id || "card-dialog"}
        className={classes("card-dialog", className, useFlexInsteadOfTransformForCentering && "mdialog-no-css-transform")}
        contentClassName={`padding-mdialog-content ${contentClassName || ""}`}
        aria-label={ariaLabel || ""}
        focusOnMount={!!focusOnMount} // Casting to boolean in case this was not provided
        onHide={() => { }} // This is called if the user clicks outside the dialog
        actions={contentActions}
        title={title && (
          <div className="card-dialog-title">
            {title}
            {this.getGridActions()}
          </div>)}
        {...!dontSpecifyWidth && { width: width || 350 }} // Sometimes we dont want to specify a width and we just want it to be defaulted so i had to make an entire new prop for that since we are defaulting width to 350 if no width is passed in, you cant even cheat it and make it width: initial because that works differently than it not being set. oh use width: unset you say? nope that didnt work either
        {...{
          //This is called if the user clicks outside the dialog
          onHide,
          visible
        }}>
        {children}
      </DialogContainer>
    );
  }
}

export const ACTION_CLOSE = "ACTION_CLOSE";
export const ACTION_APPLY = "ACTION_APPLY";
export const ACTION_CLONE = "ACTION_CLONE";
export const ACTION_DELETE = "ACTION_DELETE";
export const ACTION_ADDITIONAL_OPTIONS = "ACTION_ADDITIONAL_OPTIONS";

export default MDialog;
